/**
 * API client for Exhibit.
 * Uses token-based auth (Bearer token in Authorization header).
 */

import type {
  ApiComment,
  CommentAnchor,
  ApiReaction,
  CommentListResponse,
  CreateCommentResponse,
  AblyTokenResponse,
  ApiArtifactSharing,
  AccessSessionResponse,
  PublicArtifactResponse,
  ApiCommentAuthor,
  ArtifactStarResponse,
  ArtifactListResponse,
  InviteAcceptResponse,
  WorkspaceJoinResponse,
} from '../../../src/shared/api-contracts';
import type { AccessLevel, ArtifactStatus, SharingMode, WorkspaceMemberRole } from '../../../src/shared/types';
import { API_BASE_URL } from '../env';

export type {
  ApiComment,
  CommentAnchor,
  ApiReaction,
  ApiCommentAuthor,
};

const BASE_URL = API_BASE_URL;

const TOKEN_KEY = 'exhibit_session_token';

const WORKSPACE_KEY = 'exhibit_workspace_id';

/** Get the stored session token. */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Store the session token. */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/** Clear the stored session token. */
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Get the stored workspace ID. */
export function getWorkspaceId(): string | null {
  return localStorage.getItem(WORKSPACE_KEY);
}

/** Store the workspace ID returned by /auth/me. */
export function setWorkspaceId(id: string): void {
  localStorage.setItem(WORKSPACE_KEY, id);
}

/** Clear the stored workspace ID. */
export function clearWorkspaceId(): void {
  localStorage.removeItem(WORKSPACE_KEY);
}

interface ApiError {
  status: number;
  message: string;
  data?: unknown;
}

class ApiClientError extends Error {
  status: number;
  data?: unknown;

  constructor({ status, message, data }: ApiError) {
    super(message);
    this.status = status;
    this.data = data;
    this.name = 'ApiClientError';
  }
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (res.status === 401) {
    clearToken();
    clearWorkspaceId();
    window.location.href = '/login';
    throw new ApiClientError({ status: 401, message: 'Unauthorized' });
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiClientError({
      status: res.status,
      message: body.error || res.statusText,
      data: body,
    });
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

async function publicRequest<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const url = `${BASE_URL}${path}`;
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(url, {
    ...options,
    headers,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiClientError({
      status: res.status,
      message: body.error || res.statusText,
      data: body,
    });
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── Types ──
// Local aliases that match the shared contracts, kept for backward compat.

export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url?: string | null;
}

export interface Workspace {
  id: string;
  name: string;
  created_by?: string;
  created_at?: string;
  role?: string;
}

export interface Artifact {
  id: string;
  title: string;
  subtitle?: string;
  description?: string | null;
  status: ArtifactStatus;
  current_version: number;
  comment_count: number;
  thumbnail_url?: string;
  content_type?: string;
  latest_version_id?: string | null;
  starred: boolean;
  created_at: string;
  updated_at: string;
  author: User;
}

export interface ArtifactDetail extends Artifact {
  versions: Version[];
}

export interface Version {
  id: string;
  version_number: number;
  created_at: string;
  change_description?: string | null;
  is_current?: boolean;
  author: User;
}

export type Comment = ApiComment;
export type CommentAuthor = ApiCommentAuthor;
export type Sharing = ApiArtifactSharing;
export type ShareAccessLevel = AccessLevel;
export type ShareMode = SharingMode;

export interface ActivityEvent {
  id: string;
  type: 'artifact_shared' | 'comment' | 'reaction' | 'member_joined' | 'access_request';
  description: string;
  artifact_id?: string | null;
  artifact_title?: string | null;
  created_at: string;
  read: boolean;
  target_label?: string | null;
  actor: User;
}

export interface Member {
  id: string;
  user: User;
  role: WorkspaceMemberRole;
  joined_at: string;
}

export interface AttentionItem {
  id: string;
  type: 'comment' | 'new_version';
  artifact_id: string;
  artifact_title: string;
  count?: number;
  actor_name?: string;
}

export interface ArtifactListParams {
  sort?: 'recent' | 'alphabetical' | 'starred' | 'starred_only';
  view?: 'active' | 'archived';
  limit?: number;
  offset?: number;
  q?: string;
  owner_id?: string;
  type?: string;
  created_from?: string;
  created_to?: string;
  updated_from?: string;
  updated_to?: string;
}

// ── API Methods ──

/** Helper: returns the workspace_id query param string, or throws if missing. */
function wsParam(): string {
  const id = getWorkspaceId();
  if (!id) throw new ApiClientError({ status: 400, message: 'No workspace selected' });
  return id;
}

export const api = {
  // Auth
  getMe: async () => {
    const data = await request<{ user: User; workspace: Workspace | null }>('/auth/me');
    if (data.workspace && !getWorkspaceId()) {
      setWorkspaceId(data.workspace.id);
    }
    return data as { user: User; workspace: Workspace };
  },

  // Artifacts
  listArtifacts: (params: ArtifactListParams = {}) => {
    const search = new URLSearchParams();
    search.set('workspace_id', wsParam());
    search.set('sort', params.sort ?? 'recent');
    search.set('view', params.view ?? 'active');
    search.set('limit', String(params.limit ?? 25));
    search.set('offset', String(params.offset ?? 0));
    if (params.q) search.set('q', params.q);
    if (params.owner_id) search.set('owner_id', params.owner_id);
    if (params.type) search.set('type', params.type);
    if (params.created_from) search.set('created_from', params.created_from);
    if (params.created_to) search.set('created_to', params.created_to);
    if (params.updated_from) search.set('updated_from', params.updated_from);
    if (params.updated_to) search.set('updated_to', params.updated_to);
    return request<ArtifactListResponse>(`/api/artifacts?${search.toString()}`);
  },

  getArtifact: async (id: string) => {
    const data = await request<{ artifact: Artifact; versions: Version[]; role: string; effective_access?: AccessLevel | null }>(`/api/artifacts/${id}`);
    return {
      artifact: { ...data.artifact, versions: data.versions } as ArtifactDetail,
      role: data.role,
      effective_access: data.effective_access ?? null,
    };
  },

  getArtifactSharing: (id: string) =>
    request<{ sharing: Sharing }>(`/api/artifacts/${id}/sharing`),

  updateArtifactSharing: (
    id: string,
    data: {
      mode: ShareMode;
      workspace_access_level?: ShareAccessLevel | null;
      public_access_level?: ShareAccessLevel | null;
    },
  ) =>
    request<{ sharing: Sharing }>(`/api/artifacts/${id}/sharing`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  upsertArtifactInvite: (
    id: string,
    userId: string,
    access_level: ShareAccessLevel,
  ) =>
    request<{ sharing: Sharing }>(`/api/artifacts/${id}/sharing/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify({ access_level }),
    }),

  inviteByEmail: (id: string, email: string, access_level: ShareAccessLevel) =>
    request<{ sharing: Sharing }>(`/api/artifacts/${id}/sharing/invite`, {
      method: 'POST',
      body: JSON.stringify({ email, access_level }),
    }),

  deleteArtifactInvite: (id: string, userId: string) =>
    request<{ sharing: Sharing }>(`/api/artifacts/${id}/sharing/users/${userId}`, {
      method: 'DELETE',
    }),

  createArtifactAccessSession: (id: string) =>
    request<AccessSessionResponse>(`/api/artifacts/${id}/access-session`, {
      method: 'POST',
    }),

  starArtifact: (id: string) =>
    request<ArtifactStarResponse>(`/api/artifacts/${id}/star`, {
      method: 'PUT',
    }),

  unstarArtifact: (id: string) =>
    request<ArtifactStarResponse>(`/api/artifacts/${id}/star`, {
      method: 'DELETE',
    }),

  archiveArtifact: (id: string) =>
    request<{ ok: true; status: ArtifactStatus }>(`/api/artifacts/${id}/archive`, {
      method: 'PATCH',
    }),

  unarchiveArtifact: (id: string) =>
    request<{ ok: true; status: ArtifactStatus }>(`/api/artifacts/${id}/unarchive`, {
      method: 'PATCH',
    }),

  restoreVersion: (artifactId: string, versionId: string) =>
    request<{ version: Version }>(`/api/artifacts/${artifactId}/versions/${versionId}/restore`, {
      method: 'POST',
    }),

  // Comments
  listComments: (artifactId: string, version?: number, resolved?: boolean) => {
    const params = new URLSearchParams();
    if (version != null) params.set('version', String(version));
    if (resolved != null) params.set('resolved', String(resolved));
    const qs = params.toString();
    return request<CommentListResponse>(
      `/api/artifacts/${artifactId}/comments${qs ? `?${qs}` : ''}`,
    );
  },

  createComment: (
    artifactId: string,
    body: string,
    versionNumber: number,
    parentId?: string,
    anchor?: CommentAnchor | null,
    mentions?: Array<{ id: string; name: string }>,
  ) =>
    request<CreateCommentResponse>(`/api/artifacts/${artifactId}/comments`, {
      method: 'POST',
      body: JSON.stringify({
        body,
        version_number: versionNumber,
        parent_id: parentId,
        anchor: anchor ?? undefined,
        mentions: mentions ?? undefined,
      }),
    }),

  resolveComment: (artifactId: string, commentId: string) =>
    request<{ ok: true }>(`/api/artifacts/${artifactId}/comments/${commentId}/resolve`, { method: 'PATCH' }),

  unresolveComment: (artifactId: string, commentId: string) =>
    request<{ ok: true }>(`/api/artifacts/${artifactId}/comments/${commentId}/unresolve`, { method: 'PATCH' }),

  toggleReaction: (artifactId: string, commentId: string, emoji: string) =>
    request<{ action: 'added' | 'removed' }>(`/api/artifacts/${artifactId}/comments/${commentId}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    }),

  getPublicArtifact: (id: string) =>
    publicRequest<PublicArtifactResponse>(`/public/artifacts/${id}`),

  createPublicArtifactAccessSession: (id: string) =>
    publicRequest<AccessSessionResponse>(`/public/artifacts/${id}/access-session`, {
      method: 'POST',
    }),

  joinWorkspaceForArtifact: (id: string) =>
    publicRequest<WorkspaceJoinResponse>(`/public/artifacts/${id}/join-workspace`, {
      method: 'POST',
    }),

  listPublicComments: (artifactId: string, resolved?: boolean) => {
    const params = new URLSearchParams();
    if (resolved != null) params.set('resolved', String(resolved));
    const qs = params.toString();
    return publicRequest<CommentListResponse>(
      `/public/artifacts/${artifactId}/comments${qs ? `?${qs}` : ''}`,
    );
  },

  createPublicComment: (
    artifactId: string,
    data: {
      body: string;
      version_number: number;
      parent_id?: string;
      anchor?: CommentAnchor | null;
      guest_alias?: string;
      guest_session_id?: string;
    },
  ) =>
    publicRequest<CreateCommentResponse>(`/public/artifacts/${artifactId}/comments`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  getAblyToken: () =>
    request<AblyTokenResponse>('/api/ably/token'),

  // Inbox
  listActivity: () =>
    request<{ events: ActivityEvent[] }>(`/api/inbox?workspace_id=${wsParam()}`),

  markAllRead: () =>
    request<void>(`/api/inbox/mark-read?workspace_id=${wsParam()}`, { method: 'POST' }),

  markOneRead: (id: string) =>
    request<void>(`/api/inbox/${id}/mark-read`, { method: 'POST' }),

  getUnreadCount: () =>
    request<{ count: number }>(`/api/inbox/unread-count?workspace_id=${wsParam()}`),

  // Needs Attention
  getAttentionItems: () =>
    request<{ items: AttentionItem[] }>(`/api/attention?workspace_id=${wsParam()}`),

  // Settings
  getWorkspace: () =>
    request<{ workspace: Workspace }>(`/api/workspaces/${wsParam()}`),

  updateWorkspace: (data: { name: string }) =>
    request<{ workspace: Workspace }>(`/api/workspaces/${wsParam()}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  listMembers: () =>
    request<{ members: Member[] }>(`/api/workspaces/${wsParam()}/members`),

  updateMemberRole: (memberId: string, role: string) =>
    request<{ member: Member }>(`/api/workspaces/${wsParam()}/members/${memberId}`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    }),

  createInviteLink: (options?: { role?: WorkspaceMemberRole; email?: string }) => {
    const payload: { role?: WorkspaceMemberRole; email?: string } = {};
    if (options?.role) payload.role = options.role;
    if (options?.email) payload.email = options.email;
    const hasBody = Object.keys(payload).length > 0;
    return request<{ link: string; email_sent: boolean }>(
      `/api/workspaces/${wsParam()}/invite`,
      {
        method: 'POST',
        body: hasBody ? JSON.stringify(payload) : undefined,
      },
    );
  },

  acceptInvite: (token: string) =>
    request<InviteAcceptResponse>(`/api/invite/${token}/accept`, { method: 'POST' }),

  // Slack integration
  completeSlackOAuth: async (code: string, state: string): Promise<{ ok: boolean; error?: string }> => {
    const res = await request<{ ok: boolean; error?: string }>('/api/integrations/slack/callback', {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    });
    return res;
  },

  getSlackStatus: (): Promise<{
    connected: boolean;
    slack_team_name?: string;
    installed_by_name?: string;
    installed_at?: string;
  }> =>
    request(`/api/integrations/slack/status?workspace_id=${wsParam()}`),

  disconnectSlack: (): Promise<{ ok: boolean }> =>
    request(`/api/integrations/slack?workspace_id=${wsParam()}`, { method: 'DELETE' }),
};

export { ApiClientError };
